import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Route, useHistory } from 'react-router-dom';
import { CLIENT_TYPES } from '@medflex/utils/constants';
import LoadingView from '@medflex/components/stories/LoadingView';
import ErrorView from '@medflex/components/stories/ErrorView';
import config from '@medflex/utils/config';
import useUserProfile from '../hooks/useUserProfile';
import InactiveUserModule from '../modules/InactiveUserModule';
import FailedProfileLoadError from '../components/FailedProfileLoadError';

const UserProfileRestrictedRoute = ({ component, render }) => {
  const { userProfile, isLoadingUserProfile, errors } = useUserProfile(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const history = useHistory();

  const redirect = e => {
    if (e) e.preventDefault();
    history.push('/abmelden');
  };

  const hasProfile = useMemo(
    () => !isLoadingUserProfile && userProfile,
    [userProfile, isLoadingUserProfile],
  );

  const { exists, verified } = useMemo(() => {
    if (hasProfile) {
      if (
        config.CLIENT_TYPE === CLIENT_TYPES.MOBILE ||
        config.CLIENT_TYPE === CLIENT_TYPES.DESKTOP ||
        config.CLIENT_TYPE === CLIENT_TYPES.CHAT
      ) {
        return { exists: true, verified: true };
      }
      if (config.CLIENT_TYPE === CLIENT_TYPES.PHYSICIAN) {
        return {
          exists: userProfile.flags.isPhysician && !userProfile.flags.isNonExistent,
          verified: !userProfile.flags.unverified,
        };
      }
      return { exists: userProfile.flags.isPatient };
    }
    return { exists: false, verified: false };
  }, [userProfile, hasProfile]);

  const deactivated = hasProfile && userProfile.attributes && userProfile.attributes.deactivated;

  useEffect(() => {
    if (hasProfile) {
      if (!verified) {
        setError({
          title: t('UserProfileRestrictedRoute.errors.not_verified.title'),
          text: t('UserProfileRestrictedRoute.errors.not_verified.text'),
        });
        return;
      }

      if (!exists) {
        history.push('/abmelden');
      }
    }
  }, [hasProfile, exists, verified, errors.loadProfile, t, history]);

  if (deactivated) {
    return <InactiveUserModule />;
  }

  if (exists) {
    return <Route render={render} component={component} />;
  }

  if (error) {
    return (
      <ErrorView
        {...error}
        href="#"
        onClickButton={redirect}
        buttonLabel={t('UserProfileRestrictedRoute.errors.not_verified.loginLabel')}
      />
    );
  }

  if (errors.loadProfile) {
    return <FailedProfileLoadError />;
  }

  return (
    <LoadingView
      title={t('loading_app.title')}
      message={t('loading_app.message', { context: 'authorize' })}
    />
  );
};

UserProfileRestrictedRoute.propTypes = {
  render: PropTypes.func,
  component: PropTypes.func,
};

export default UserProfileRestrictedRoute;
